// src/utils/keycloakSync.ts
import type Keycloak from 'keycloak-js';
import { saveAuthData, clearAuthData } from '@/utils/auth';
import { fetchUsersMe } from '@/utils/usersMe';
import type { UserRole } from '@/types/models';
import { documentationOnlyFromToken, setDocumentationOnly } from '@/features/developers/access';

/**
 * Map Keycloak client roles to Insight app roles.
 * Keycloak uses: superadmin, admin, chain-director, hotel-director, viewer,
 * plus the new `insight:receptionist` client role (Decision 3 of the
 * hotel-operations redesign). Accept both the prefixed and bare spelling
 * until Cris confirms which one is actually assigned in the realm.
 * Insight uses: SuperAdmin, Admin, GroupAdmin, HotelAdmin, Receptionist, Viewer
 */
const KC_ROLE_MAP: Record<string, UserRole> = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  'chain-director': 'GroupAdmin',
  'hotel-director': 'HotelAdmin',
  viewer: 'Viewer',
  receptionist: 'Receptionist',
  'insight:receptionist': 'Receptionist',
};

/** Highest role first — a user carrying several client roles gets the top one. */
const ROLE_PRIORITY: UserRole[] = ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'];

/**
 * Client roles that grant a capability rather than a role (Decision 7). Only
 * 'technical' exists today, spelled `insight:technical` per Decision 3 (bare
 * `technical` accepted too, same reasoning as the receptionist role above).
 */
const KC_CAPABILITY_MAP: Record<string, string> = {
  technical: 'technical',
  'insight:technical': 'technical',
};

function extractCapabilities(roles: string[]): string[] {
  const found = new Set<string>();
  for (const r of roles) {
    const capability = KC_CAPABILITY_MAP[r];
    if (capability) found.add(capability);
  }
  return Array.from(found);
}

/**
 * Extract the Insight semantic role from Keycloak client roles.
 */
function mapRole(roles: string[]): UserRole {
  const mapped = roles
    .map((r) => KC_ROLE_MAP[r])
    .filter((r): r is UserRole => !!r);
  for (const role of ROLE_PRIORITY) {
    if (mapped.includes(role)) return role;
  }
  return 'Viewer';
}

/**
 * Extract chain-id-{uuid} and hotel-id-{uuid} from client roles.
 */
function extractScope(roles: string[]): {
  chainIds: string[];
  hotelIds: string[];
} {
  const chainIds: string[] = [];
  const hotelIds: string[] = [];

  for (const r of roles) {
    if (r.startsWith('chain-id-')) {
      chainIds.push(r.slice('chain-id-'.length));
    } else if (r.startsWith('hotel-id-')) {
      hotelIds.push(r.slice('hotel-id-'.length));
    }
  }

  return { chainIds, hotelIds };
}

/**
 * Sync Keycloak token data into localStorage using saveAuthData().
 *
 * This bridges the keycloak-js adapter with the existing localStorage-based
 * auth utilities (getCurrentUser, getResourcesForReactAdmin, etc.).
 *
 * Call this:
 * - After initial keycloak.init() succeeds
 * - After keycloak.onAuthRefreshSuccess (token silently refreshed)
 */
export function syncKeycloakToLocalStorage(keycloak: Keycloak): void {
  const token = keycloak.token;
  const parsed = keycloak.tokenParsed;

  if (!token || !parsed) {
    clearAuthData();
    return;
  }

  const clientId = keycloak.clientId ?? 'insight';
  const appRoles: string[] =
    (parsed as any).resource_access?.[clientId]?.roles ?? [];

  const role = mapRole(appRoles);
  const { chainIds, hotelIds } = extractScope(appRoles);
  const capabilities = extractCapabilities(appRoles);

  const expiresIn = parsed.exp
    ? parsed.exp - Math.floor(Date.now() / 1000)
    : 3600;

  saveAuthData({
    token,
    user: {
      id: parsed.sub ?? '',
      name: (parsed as any).name ?? (parsed as any).preferred_username ?? '',
      email: (parsed as any).email ?? '',
      role,
      hotelId: hotelIds[0],
      groupId: chainIds[0],
      assignedHotels: hotelIds,
      assignedGroups: chainIds,
      capabilities,
    },
    expiresIn,
  });

  localStorage.setItem('auth_method', 'keycloak');

  // A documentation realm role and no application role: this person reads the Developers section
  // and nothing else. Known from the token at once; `users/me` below confirms it.
  setDocumentationOnly(documentationOnlyFromToken(parsed, clientId));

  // Store identity provider if user logged in via external IdP (Microsoft, Google, etc.)
  const idp = (parsed as any).identity_provider;
  if (idp) {
    localStorage.setItem('identity_provider', idp);
  } else {
    localStorage.removeItem('identity_provider');
  }

  // Decision 8: `GET /api/v1/ui/users/me` is the authoritative source for
  // capabilities once the API lane ships it. Fire-and-forget — the token
  // decoding above already gives every screen a working set of permissions,
  // this only refines the 'technical' capability when the endpoint responds.
  void fetchUsersMe().then((me) => {
    if (me) {
      localStorage.setItem('capabilities', JSON.stringify(me.capabilities));
      setDocumentationOnly(me.documentationOnly);
    }
  });
}
